import React from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Package, MessageSquareQuote, FileText,
  ShoppingCart, CreditCard, LogOut, ShieldCheck, Loader2, ExternalLink, Youtube, PhoneCall, KeyRound,
} from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/content", label: "Website Content", icon: FileText },
  { to: "/admin/media", label: "Videos & Stories", icon: Youtube },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/leads", label: "Leads", icon: PhoneCall },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/settings", label: "Change Password", icon: KeyRound },
];

const AdminLayout = () => {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <aside className="w-60 bg-slate-900 text-slate-300 flex flex-col fixed inset-y-0">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800 text-white">
          <ShieldCheck className="w-6 h-6 text-amber-400" />
          <span className="font-semibold">Adrusta Admin</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-amber-500 text-slate-900" : "hover:bg-slate-800 hover:text-white"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-slate-800 hover:text-white">
            <ExternalLink className="w-4 h-4" /> View Website
          </a>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-slate-800 hover:text-white">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-60">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-10">
          <div className="text-sm text-slate-500">Management Console</div>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{(admin.name || admin.email || "A").charAt(0).toUpperCase()}</div>
            <div className="text-right">
              <div className="font-medium text-slate-800 leading-tight">{admin.name}</div>
              <div className="text-xs text-slate-400 leading-tight">{admin.role}</div>
            </div>
          </div>
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
